import { Modal } from 'react-daisyui';
import { Button } from '@leadsales/design-system/react';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { Trans, useTranslation } from 'react-i18next';
import '../../Wallet.confirmDialog.styles.scss';

/**
 * Confirmación de REEMPLAZO de la recarga automática (RET-1692, D-6).
 *
 * Solo puede haber una recarga automática activa (SSOT §4), así que dar de alta
 * una nueva teniendo otra **cancela la anterior**. El modal de recarga ya lo
 * avisa inline; esto es el "sí, entendí" que el criterio pide explícitamente,
 * porque reemplazar deshace algo que el cliente configuró antes y no debe
 * ocurrir por un clic de inercia.
 *
 * Lo que el cuerpo tiene que decir, y por qué cada parte:
 *
 *   - **qué reemplaza**: el monto de la que ya está corriendo, resaltado. Es
 *     contra lo que el cliente compara antes de aceptar.
 *   - **desde cuándo**: el reemplazo entra el PRÓXIMO ciclo, no hoy. Sin decirlo,
 *     el cliente asume que el cambio es inmediato y se queda esperando un cobro
 *     que no llega.
 *   - **no hay cobro doble**: es la duda concreta que frena la decisión. Quien
 *     está reemplazando ya tiene un cargo configurado y lo primero que teme es
 *     que le cobren las dos cosas este mes.
 *
 * Las tres van en UN párrafo, como el prototipo. La cadencia no se nombra: el
 * prototipo dice "al mes" porque se dibujó para un plan mensual, y esta recarga
 * hereda la periodicidad del plan — escribirlo fijo mentiría en trimestral,
 * semestral y anual.
 */
export default function ReplaceRecurringModal(props: {
  isOpen: boolean;
  isBusy?: boolean;
  /** La recarga que se va a reemplazar, ya formateada por el conectado. */
  currentAmountLabel: string;
  /** Cuándo entra la nueva. Vacío si el back no dio fecha. */
  nextChargeLabel: string;
  /**
   * RET-2425 (RC-22): the replacement between the two automatic types. The one
   * in force turns off AT ONCE, not at the next cycle, so each variant has its
   * own sentence. `recurring` (default) is today's replacement.
   *
   * - `thresholdReplacesRecurring`: `currentAmountLabel` is the recurring
   *   top-up's amount.
   * - `recurringReplacesThreshold`: `currentAmountLabel` is the threshold
   *   top-up's amount and `nextChargeLabel` the recurring one's first charge;
   *   without a date, the sentence that names no day.
   */
  variant?:
    'recurring' | 'thresholdReplacesRecurring' | 'recurringReplacesThreshold';
  /**
   * RET-2425 (RC-20): the balance is already below the limit, so confirming
   * also charges this amount, formatted. The body says it and the button says
   * «… y recargar». Null: confirming does not charge.
   */
  chargeNowLabel?: string | null;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const {
    isOpen,
    isBusy = false,
    currentAmountLabel,
    nextChargeLabel,
    variant = 'recurring',
    chargeNowLabel = null,
    onCancel,
    onConfirm,
  } = props;
  const { t } = useTranslation('wallet');

  // No se monta cerrado. `Modal.Legacy` deja su contenido en el DOM aunque
  // `open` sea false, y para una CONFIRMACIÓN eso no es cosmético: el texto
  // "¿reemplazar tu recarga automática?" queda alcanzable por un lector de
  // pantalla, y sus botones por cualquier consulta que no filtre visibilidad.
  // El modal de recarga sí puede permitírselo porque su caller lo monta
  // condicionalmente; este vive siempre dentro de aquél.
  if (!isOpen) return null;

  const bodyKey =
    variant === 'thresholdReplacesRecurring'
      ? 'topUp.replace.thresholdConfirmBody'
      : variant === 'recurringReplacesThreshold'
        ? nextChargeLabel
          ? 'topUp.replace.recurringReplacesThresholdConfirmBody'
          : 'topUp.replace.recurringReplacesThresholdConfirmBodyNoDate'
        : nextChargeLabel
          ? 'topUp.replace.confirmBody'
          : 'topUp.replace.confirmBodyNoDate';

  return (
    <Modal.Legacy
      open={isOpen}
      onClickBackdrop={onCancel}
      // Las medidas de la CAJA van como utilidades en el className, no con
      // `@apply` en la hoja: comparten elemento con `.modal-box` de DaisyUI
      // —misma especificidad— y ahí sólo decide el orden del bundle. En la
      // capa de utilidades no hay empate que perder. Es lo que ya hace el
      // modal de recarga. 27.5rem = 440px, el ancho del prototipo.
      className="WalletConfirmDialog max-w-[27.5rem] rounded-2xl p-6"
    >
      <div
        className="WalletConfirmDialog__Body"
        data-testid="wallet-replace-recurring-modal"
      >
        {/* Decorativa: la pregunta ya está en el título, así que anunciarla
            sería ruido antes de la decisión. */}
        <span className="WalletConfirmDialog__Icon" aria-hidden="true">
          <ArrowPathIcon />
        </span>

        <h3 className="WalletConfirmDialog__Title">
          {t('topUp.replace.confirmTitle')}
        </h3>

        <p
          className="WalletConfirmDialog__Text"
          data-testid="wallet-replace-recurring-body"
        >
          {/*
            El monto de la recarga vigente va resaltado, igual que en el aviso
            del modal de recarga: es el dato contra el que el cliente compara
            antes de aceptar. Mismo camino (`<0>…</0>` + `Trans`) y por la
            misma razón — el orden de las palabras alrededor del monto cambia
            entre locales, así que partir la frase la rompería.

            Las tres dudas viven en UN párrafo, como el prototipo, pero siguen
            dichas las tres: qué reemplaza, desde cuándo, y que no hay cobro
            doble este ciclo. Esa última es la que frena la decisión y por eso
            cierra la frase en vez de desaparecer.

            Sin fecha se usa la OTRA llave, no la misma con el hueco vacío:
            `nextCharge` falta cuando el back no resuelve la suscripción del
            plan, y la frase de siempre dejaría un paréntesis vacío a media
            promesa de cobro. Es el mismo borde que el cuerpo del modal de
            recarga ya cierra con su variante sin fecha.
          */}
          <Trans
            t={t}
            i18nKey={bodyKey}
            values={{ amount: currentAmountLabel, date: nextChargeLabel }}
            components={[<strong key="0" className="font-semibold" />]}
          />
          {chargeNowLabel ? (
            <> {t('topUp.replace.chargeNow', { amount: chargeNowLabel })}</>
          ) : null}
        </p>

        <div className="WalletConfirmDialog__Actions">
          {/* Contorneada, no fantasma: en el prototipo las dos salidas se ven
              como botones. La de cancelar sin contorno se leía como enlace, y
              la salida segura de un diálogo no debe parecer menos pulsable que
              la que deshace lo ya configurado. */}
          <Button
            color="tertiary"
            size="md"
            onClick={onCancel}
            disabled={isBusy}
            data-testid="wallet-replace-recurring-cancel"
          >
            {t('topUp.replace.confirmCancel')}
          </Button>
          <Button
            color="primary"
            size="md"
            loading={isBusy}
            disabled={isBusy}
            onClick={onConfirm}
            data-testid="wallet-replace-recurring-confirm"
          >
            {chargeNowLabel
              ? t('topUp.replace.confirmAcceptAndCharge')
              : t('topUp.replace.confirmAccept')}
          </Button>
        </div>
      </div>
    </Modal.Legacy>
  );
}
